import NumberFlow from "@number-flow/react";
import { cn } from "cn";
import { Button } from "@/components/ui/button/button";
import { Card } from "@/components/ui/card";
import { BwLoadingLine } from "@/components/ui/icons";

export interface PricingTier {
  id: string;
  name: string;
  price: Record<string, number | string>;
  description: string;
  features: string[];
  cta: string;
}

interface PricingCardProps {
  tier: PricingTier;
  paymentFrequency: string;
  onSelect?: () => void;
  isSelected: boolean;
  isLoading: boolean;
}

export function PricingCard({
  tier,
  paymentFrequency,
  onSelect,
  isSelected = false,
  isLoading = false,
}: PricingCardProps) {
  const price = tier.price[paymentFrequency];

  return (
    <Card
      className={cn(
        /* sorted */
        "relative flex overflow-hidden",
        "flex-col gap-4",
        "p-6",
        "border border-border-dark-low shadow-none",
        isSelected
          ? "bg-foreground text-background"
          : "bg-background text-foreground",
        isSelected && "border-2 border-border-primary-max shadow-lg",
      )}
    >
      <div>
        <h2 className="text-xl leading-7 font-medium mb-1">{tier.name}</h2>
        <h3 className="text-base leading-normal font-normal text-text-dark-high">
          {tier.description}
        </h3>
      </div>
      <div className="relative h-12">
        {typeof price === "number" ? (
          <div className="flex items-baseline">
            <NumberFlow
              format={{
                style: "currency",
                currency: "USD",
                trailingZeroDisplay: "stripIfInteger",
              }}
              value={price}
              className="text-3xl leading-9 font-semibold"
            />
            <p className="text-sm leading-5 font-normal text-text-dark-low">
              /{paymentFrequency}
            </p>
          </div>
        ) : (
          <h1 className="text-4xl font-medium">{price}</h1>
        )}
      </div>

      <div className="flex-1 space-y-2">
        <Button
          kind={"solid"}
          className="w-full"
          onClick={onSelect}
          disabled={isLoading}
          size="xl"
        >
          {isLoading && <BwLoadingLine size={20} className="animate-spin" />}
          {tier.cta}
        </Button>
      </div>
    </Card>
  );
}
